import React, { useState, useRef, useEffect } from 'react';
import { ProductImage } from 'anf-core-react';

const ImageMagnifier = ({
  originalSrc, zoomSrc, alt, onClick, onError, onLoad, ariaLabel = '',
}) => {
  const [zoomActive, setZoomActive] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 }); // Percentage position
  const containerRef = useRef(null);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    // Preload the zoomed image
    const img = new Image();
    img.src = zoomSrc;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setZoomActive(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [zoomSrc]);

  // Adjust position based on keyboard navigation
  const handleKeyDown = (e) => {
    if (!zoomActive) return;

    const step = 5; // Movement step in percentage
    const { x, y } = position;

    switch (e.key) {
      case 'ArrowUp':
        setPosition({ x, y: Math.max(0, y - step) });
        break;
      case 'ArrowDown':
        setPosition({ x, y: Math.min(100, y + step) });
        break;
      case 'ArrowLeft':
        setPosition({ x: Math.max(0, x - step), y });
        break;
      case 'ArrowRight':
        setPosition({ x: Math.min(100, x + step), y });
        break;
      case 'Escape':
        setZoomActive(false);
        break;
      default:
        break;
    }
  };

  // Adjust position based on mouse movement
  const handleMouseMove = (e) => {
    if (!zoomActive) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPosition({ x: Math.min(100, Math.max(0, x)), y: Math.min(100, Math.max(0, y)) });
  };

  const activateZoom = (e) => {
    if (e.type === 'mouseenter' || userInteractedRef.current) {
      setZoomActive(true);
    }
  };

  const handleUserInteraction = () => {
    userInteractedRef.current = true;
  };

  const deactivateZoom = () => {
    setZoomActive(false);
    userInteractedRef.current = false;
  };

  // Focus container when zoom is activated
  useEffect(() => {
    if (zoomActive) {
      containerRef.current.focus();
    }
  }, [zoomActive]);

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      onKeyDown={(e) => {
        handleUserInteraction();
        handleKeyDown(e);
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={activateZoom}
      onMouseLeave={deactivateZoom}
      onFocus={activateZoom}
      onBlur={deactivateZoom}
      aria-label={ariaLabel}
      role="button"
      className="product-image-react magnifier-container"
      onClick={onClick}
    >
      {/* Original image */}
      {!zoomActive && (
        <ProductImage
          src={originalSrc}
          alt={alt}
          classList="quickview-magnifier-image"
          onClick={onClick}
          onError={onError}
          onLoad={onLoad}
          data-size=""
        />
      )}
      {/* High-resolution zoomed image */}

      <img
        src={zoomSrc}
        alt={alt}
        className="quickview-magnifier-zoomed-image"
        style={{
          top: `${-position.y}%`,
          left: `${-position.x}%`,
          display: zoomActive ? 'block' : 'none',
        }}
      />
    </div>
  );
};

export default ImageMagnifier;
